import { Dictionary } from '@feedyou/utils'
import { GlobalStorage } from '@feedyou/schema'
import {
  composeValidators,
  evaluateCriteria,
  findBotVariableMatchingText,
  findReadonlyStorageMatchingText,
  isReadonlyGlobalStorage
} from './utils'
import { isValidVariableName } from '../regex'

export type AdditionalValidationParams = {
  botVariables: object | undefined
  globalStorages: Dictionary<string, GlobalStorage> | undefined
}

export type ValidatorCriteria =
  | RegExp
  | ((data: string, additionalValidationParams: AdditionalValidationParams) => boolean)

// Criteria can be left out to only change the message for built-in rjsf criteria
export type ValidatorDefinition = {
  criteria?: ValidatorCriteria
  getErrorMessage: (
    validatedText: string,
    additionalValidationParams: AdditionalValidationParams
  ) => string
}

const url: ValidatorDefinition = {
  // Built-in validator - does not have criteria defined by us
  getErrorMessage: () => 'Field must be a valid url address'
}

const cannotBeginOrEndWithDash: ValidatorDefinition = {
  criteria: new RegExp('^[^-].*[^-?]$|^[^-]$|^$'),
  getErrorMessage: () => 'Dash (-) is not allowed at the beginning and at the end'
}

const cannotBeginOrEndWithDot: ValidatorDefinition = {
  criteria: new RegExp('^[^.].*[^.?]$|^[^.]$|^$'),
  getErrorMessage: () => 'Dot is not allowed at the beginning and at the end'
}

const cannotBeginOrEndWithAtSign: ValidatorDefinition = {
  criteria: new RegExp('^[^@].*[^@?]$|^[^@]$|^$'),
  getErrorMessage: () => 'At sign (@) is not allowed at the beginning and at the end'
}

const cannotBeginOrEndWithColon: ValidatorDefinition = {
  criteria: new RegExp('^[^:].*[^:?]$|^[^:]$|^$'),
  getErrorMessage: () => 'Colon (:) is not allowed at the beginning and at the end'
}

const cannotBeginOrEndWithUnderscore: ValidatorDefinition = {
  criteria: new RegExp('^[^_].*[^_?]$|^[^_]$|^$'),
  getErrorMessage: () => 'Underscore (_) is not allowed at the beginning and at the end'
}

const cannotContainUpperCaseLettersOrCertainSymbols: ValidatorDefinition = {
  criteria: new RegExp('^[0-9a-z-_@:.]*$'),
  getErrorMessage: () =>
    'Please use only lower case letters, numbers, hyphens (-), underscores (_), colons (:) or at signs (@)'
}

const alphanumericWithDashes: ValidatorDefinition = {
  criteria: new RegExp('^[0-9a-z-@:]*$'),
  getErrorMessage: () => 'Please only use lowercase letters, numbers, hyphens, colon or @'
}

const cannotIncludeForwardSlash: ValidatorDefinition = {
  criteria: new RegExp('^[^/]+$'),
  getErrorMessage: () => 'Forward slash character is not allowed'
}

const cannotIncludeDot: ValidatorDefinition = {
  criteria: text => !text?.includes('.'),
  getErrorMessage: () =>
    'Provided storage name includes a dot (.) - you cannot write deep into storage properties.'
}

const storageNameValidationRulesForSymbols = composeValidators([
  cannotBeginOrEndWithDash,
  cannotBeginOrEndWithColon,
  cannotBeginOrEndWithAtSign,
  cannotBeginOrEndWithUnderscore
])

const storageName: ValidatorDefinition = composeValidators([
  cannotBeginOrEndWithDot,
  storageNameValidationRulesForSymbols,
  // Order matters here - we have verbalized all the other potential infractions, now we know
  // user is using an invalid character
  {
    criteria: text => !!isValidVariableName(text),
    getErrorMessage: () =>
      'Please use only letters, numbers, hyphens (-), underscores (_), colons (:), dots (.) or at signs (@)'
  }
])

const notAReadonlyStorageName: ValidatorDefinition = composeValidators([
  {
    criteria: validatedText => !findReadonlyStorageMatchingText(validatedText),
    getErrorMessage: validatedText => {
      const storageName = validatedText.toLowerCase() || 'This one'
      return `"${storageName}" is a read-only system storage and cannot be written into. Please choose a different storage.`
    }
  },
  {
    criteria: (validatedText, additionalValidationParams) => {
      return !findBotVariableMatchingText(
        validatedText,
        additionalValidationParams?.['botVariables']
      )
    },
    getErrorMessage: validatedText => {
      const storageName = validatedText.toLowerCase() || 'This one'
      return `"${storageName}" is read-only, because it is defined under botFlow.storages property in bot json`
    }
  },
  {
    criteria: (validatedText, additionalValidationParams) =>
      !isReadonlyGlobalStorage(validatedText, additionalValidationParams?.['globalStorages']),
    getErrorMessage: validatedText => {
      const storageName = validatedText.toLowerCase() || 'This storage'
      return `{${storageName}} is a read-only global storage. Please set it as non-read-only or choose a different storage`
    }
  }
])

const cannotIncludeDotAndStartOrEndWithCertainSymbols = composeValidators([
  cannotIncludeDot,
  storageNameValidationRulesForSymbols
])

const writableStorageName = composeValidators([
  notAReadonlyStorageName,
  cannotIncludeDotAndStartOrEndWithCertainSymbols,
  {
    criteria: text => !!isValidVariableName(text),
    getErrorMessage: () =>
      'Please use only letters, numbers, hyphens (-), underscores (_), colons (:) or at signs (@)'
  }
])

const writableDtmfDigits = {
  criteria: new RegExp('^[\\S][0-9*#ABCD]*$'),
  getErrorMessage: () => 'Please only use numbers, letters A, B, C, D or symbols *, #'
}

const cannotBeginWithWhiteSpace = {
  criteria: new RegExp('^[^\\s*$]'),
  getErrorMessage: () => 'Please use a string other than a white space at the beginning of the text'
}

const azureAiSearchIndexName: ValidatorDefinition = {
  criteria: new RegExp('^[a-z0-9]([a-z0-9-]{0,126}[a-z0-9])?$'),
  getErrorMessage: () =>
    'Index name must only contain lowercase letters, digits or dashes, cannot start or end with dashes and is limited to 128 characters'
}

const globalStorageName: ValidatorDefinition = composeValidators([
  cannotContainUpperCaseLettersOrCertainSymbols,
  cannotIncludeDotAndStartOrEndWithCertainSymbols
])

export const formatValidators = {
  url,
  cannotBeginOrEndWithDash,
  notAReadonlyStorageName,
  alphanumericWithDashes,
  storageName,
  globalStorageName,
  writableStorageName,
  writableDtmfDigits,
  cannotIncludeForwardSlash,
  cannotBeginWithWhiteSpace,
  azureAiSearchIndexName
} as const

export type FormatValidatorNames = keyof typeof formatValidators

export const evaluateFormatAdherence = (
  format: FormatValidatorNames,
  text: string,
  params: AdditionalValidationParams
) => {
  const validator = formatValidators[format]
  return evaluateCriteria(validator.criteria, text, params)
}

export const getFormatAdherenceError = (
  format: FormatValidatorNames,
  text: string,
  params: AdditionalValidationParams
) => {
  const validator = formatValidators[format]
  return validator.getErrorMessage(text, params)
}
